import type { CSSProperties } from 'react';
import type { ChartPoint } from '../ColumnChart/chart-point.ts';
import { ChartTable } from '../ChartTable/ChartTable.tsx';
import { EmptyState } from '../EmptyState/EmptyState.tsx';
import { Skeleton } from '../Skeleton/Skeleton.tsx';
import styles from './BarList.module.css';

export type BarListProps = {
  label: string;
  items: readonly ChartPoint[];
  emptyTitle: string;
  loading?: boolean;
};

const shareOf = (value: number, highest: number): CSSProperties =>
  ({ '--bar-share': String(highest > 0 ? Math.max(value, 0) / highest : 0) }) as CSSProperties;

export const BarList = ({ label, items, emptyTitle, loading = false }: BarListProps) => {
  if (loading) return <Skeleton height="14rem" />;
  const highest = Math.max(0, ...items.map((item) => item.value));
  if (highest <= 0) return <EmptyState title={emptyTitle} />;
  return (
    <figure className={styles['list']}>
      <figcaption className="visually-hidden">{label}</figcaption>
      <ol className={styles['rows']} aria-hidden="true">
        {items.map((item) => (
          <li key={item.key} className={styles['row']} title={`${item.label} · ${item.display}`}>
            <span className={styles['name']}>{item.label}</span>
            <span className={styles['value']}>{item.display}</span>
            <span className={styles['bar']} style={shareOf(item.value, highest)} />
          </li>
        ))}
      </ol>
      <ChartTable label={label} points={items} />
    </figure>
  );
};
